import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { cn } from "~/lib/utils";
import { FadeIn } from "~/components/FadeIn";
import { ToasterVariant, ToasterVariants } from "../../store/types";

// // // //

interface Props {
    variant: ToasterVariant;
    message: string;
    duration?: number;
    dismissible?: boolean;
    onRemove: () => void;
}

export const Toaster: React.FC<Props> = ({
    message,
    variant,
    dismissible = true,
    duration = 5000,
    onRemove,
}) => {
    useEffect(() => {
        // Remove the toaster after timeout
        const timer = setTimeout(() => {
            onRemove();
        }, duration);
        return () => clearTimeout(timer);
    }, [duration]);

    const getColorClass = () => {
        switch (variant) {
            case ToasterVariants.success:
                return "bg-green-50 border-green-600 text-green-600";
            case ToasterVariants.warning:
                return "bg-yellow-50 border-yellow-600 text-yellow-600";
            case ToasterVariants.danger:
                return "bg-red-50 border-red-600 text-red-600";
            case ToasterVariants.primary:
            default:
                return "bg-blue-50 border-blue-600 text-blue-600";
        }
    };

    const getColorHoverClass = () => {
        switch (variant) {
            case ToasterVariants.success:
                return "hover:bg-green-100 focus:ring-offset-green-50 focus:ring-green-600";
            case ToasterVariants.warning:
                return "hover:bg-yellow-100 focus:ring-offset-yellow-50 focus:ring-yellow-600";
            case ToasterVariants.danger:
                return "hover:bg-red-100 focus:ring-offset-red-50 focus:ring-red-600";
            case ToasterVariants.primary:
            default:
                return "hover:bg-blue-100 focus:ring-offset-blue-50 focus:ring-blue-600";
        }
    };

    return (
        <FadeIn>
            <div
                className={cn(
                    "rounded-md p-4 w-120 select-none border",
                    getColorClass(),
                )}
            >
                <div className="flex">
                    <div className="shrink-0">
                        <FontAwesomeIcon
                            className="h-5 w-5"
                            icon={faInfoCircle}
                            aria-hidden="true"
                        />
                    </div>
                    <div className="ml-3">
                        <p className="text-sm">{message}</p>
                    </div>
                    {dismissible && (
                        <div className="ml-auto pl-3">
                            <div className="-mx-1.5 -my-1.5">
                                <button
                                    type="button"
                                    className={cn(
                                        "inline-flex rounded-md p-1.5 focus:outline-hidden focus:ring-2 focus:ring-offset-2",
                                        getColorHoverClass(),
                                    )}
                                    onClick={() => {
                                        onRemove();
                                    }}
                                >
                                    <span className="sr-only">Dismiss</span>
                                    <FontAwesomeIcon
                                        className="h-5"
                                        icon={faTimes}
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FadeIn>
    );
};
